import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { catchError, of } from 'rxjs';

import { formatPersonNameLastFirst } from '@app/shared/util/person-name';
import { createTerminologyUsageSignals } from '@app/shared/terminology/terminology-usage-signals';
import { ToastService } from '@app/shared/services/toast.service';
import { PHMS_HEARING_TERMINOLOGY } from '../phms-hearing.terminology';
import {
  ProceedingCalendarRow,
  RESCHEDULE_BLOCKED_TOAST_TITLE,
  ScheduleHearingRequest,
  ScheduleHearingResult,
  scheduleWriteErrorMessage,
} from './hearing-schedule.models';
import { HearingScheduleService } from './hearing-schedule.service';
import { schedulingDateIssue } from './first-available-date';

export interface RescheduleHearingDialogData {
  hearing: ProceedingCalendarRow;
}

/**
 * "Reschedule Hearing" dialog (story 1445): lets a scheduler change an already-scheduled hearing's
 * date, time, and/or location. Opens prefilled from the current schedule; Save posts the new window
 * to the dedicated reschedule endpoint (server-gated on workflow_runtime.transition, running the
 * same slot-conflict validation as initial scheduling, and appending a durable schedule-history
 * row of the prior + new windows). Closes with the write result on success so the caller reloads
 * the calendar; a rejection raises the shared "Cannot reschedule" EZ notification toast (identical
 * to the drag/drop calendar path and the Schedule dialog) and leaves the dialog open so the
 * scheduler can adjust. CDK Dialog provides the focus trap, mirroring the Create Packet modal pattern.
 */
@Component({
  selector: 'phms-reschedule-hearing-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './reschedule-hearing-dialog.component.html',
  styleUrl: './reschedule-hearing-dialog.component.scss',
})
export class RescheduleHearingDialogComponent {
  readonly hearingText = createTerminologyUsageSignals(
    PHMS_HEARING_TERMINOLOGY.rescheduleDialog,
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly scheduleService = inject(HearingScheduleService);
  private readonly toast = inject(ToastService);
  private readonly dialogRef = inject(
    DialogRef<ScheduleHearingResult | undefined>,
  );
  readonly data = inject<RescheduleHearingDialogData>(DIALOG_DATA);

  /** Header renders the person as "Last, First Middle" (#1436); the raw title stays on the row. */
  readonly formatPersonName = formatPersonNameLastFirst;

  readonly isSaving = signal(false);

  // Editable fields, prefilled from the hearing's current schedule (UTC → local for the inputs).
  date = toDateInput(this.data.hearing.scheduledStartUtc);
  startTime = toTimeInput(this.data.hearing.scheduledStartUtc);
  endTime = toTimeInput(this.data.hearing.scheduledEndUtc);
  locationName = this.data.hearing.locationName ?? '';
  roomName = this.data.hearing.roomName ?? '';

  @HostListener('keydown.escape')
  onEscape(): void {
    this.cancel();
  }

  cancel(): void {
    if (!this.isSaving()) {
      this.dialogRef.close(undefined);
    }
  }

  save(): void {
    if (this.isSaving()) {
      return;
    }

    const window = this.buildWindow();
    if (!window) {
      return;
    }

    const body: ScheduleHearingRequest = {
      ...(this.data.hearing.schedulingContext
        ? { schedulingContext: this.data.hearing.schedulingContext }
        : {}),
      scheduledStartUtc: window.startUtc,
      scheduledEndUtc: window.endUtc,
      timeZoneId: this.data.hearing.timeZoneId ?? null,
      locationName: this.locationName.trim() || null,
      roomName: this.roomName.trim() || null,
      // Preserve the existing format/slot binding; this dialog changes only date/time/location.
      videoConferenceUrl: this.data.hearing.videoConferenceUrl ?? null,
      calendarSlotId: this.data.hearing.calendarSlotId ?? null,
    };

    this.isSaving.set(true);
    this.scheduleService
      .rescheduleHearing(this.data.hearing.scheduledProceedingId, body)
      .pipe(
        catchError((error: unknown) => {
          this.showError(error);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.isSaving.set(false);
        if (result !== null) {
          this.dialogRef.close(result);
        }
      });
  }

  /**
   * Combines the date + time inputs into an ISO UTC window, surfacing inline validation instead of
   * posting an invalid request. Returns null (with the error signals set) when invalid.
   */
  private buildWindow(): { startUtc: string; endUtc: string } | null {
    const start = combineDateTime(this.date, this.startTime);
    const end = combineDateTime(this.date, this.endTime);
    if (!start || !end) {
      this.notifyError(this.hearingText.chooseDateTime());
      return null;
    }
    if (end.getTime() <= start.getTime()) {
      this.notifyError('The end time must be after the start time.');
      return null;
    }
    if (isBeforeToday(start)) {
      this.notifyError(this.hearingText.pastDate());
      return null;
    }
    const issue = schedulingDateIssue(this.data.hearing, start);
    if (issue) {
      this.notifyError(issue);
      return null;
    }
    return { startUtc: start.toISOString(), endUtc: end.toISOString() };
  }

  /**
   * Surfaces a failed reschedule write as the shared "Cannot reschedule" EZ notification toast —
   * identical to the drag/drop calendar path and the Schedule dialog. The specific server reason
   * (slot/room/resource conflict, after-due-date, lead time, permission, or HTTP status) rides in
   * the toast body via the shared mapper; the dialog stays open so the scheduler can pick a
   * different window.
   */
  private showError(error: unknown): void {
    this.notifyError(
      scheduleWriteErrorMessage(error, {
        permissionDenied: this.hearingText.permissionDenied(),
        conflict: this.hearingText.conflict(),
        failedTitle: this.hearingText.failedTitle(),
        failed: this.hearingText.failed(),
      }),
    );
  }

  /** Raises the shared "Cannot reschedule" EZ notification with the given reason. */
  private notifyError(message: string): void {
    this.toast.showError(message, RESCHEDULE_BLOCKED_TOAST_TITLE);
  }
}

/**
 * In-dialog message for a hearing placed on a day that has already passed. Same wording the
 * schedule/reschedule endpoints return, so the pre-post check and the authoritative server
 * backstop read identically.
 */
// Exported for the sibling Schedule Hearing dialog, which shares this dialog's date/time model.
/**
 * True when the chosen start falls on a day before today (local). Day-granularity on purpose,
 * matching the calendar's past-day drop guard: a same-day hearing stays allowed, yesterday and
 * earlier are rejected before posting. The server enforces the same rule against UTC as the
 * authoritative backstop (400 ValidationProblem rendered in-dialog).
 */
export function isBeforeToday(start: Date): boolean {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return start.getTime() < startOfToday.getTime();
}

/** ISO UTC timestamp → local "yyyy-MM-dd" for the date input; empty when absent/invalid. */
// Exported for the sibling Schedule Hearing dialog, which shares this dialog's date/time model.
export function toDateInput(value: string | null | undefined): string {
  const date = parseDate(value);
  if (!date) {
    return '';
  }
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** ISO UTC timestamp → local "HH:mm" for the time input; empty when absent/invalid. */
export function toTimeInput(value: string | null | undefined): string {
  const date = parseDate(value);
  if (!date) {
    return '';
  }
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Combines local "yyyy-MM-dd" + "HH:mm" inputs into a local Date; null when either is missing. */
export function combineDateTime(
  dateInput: string,
  timeInput: string,
): Date | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateInput.trim());
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeInput.trim());
  if (!dateMatch || !timeMatch) {
    return null;
  }
  const combined = new Date(
    Number(dateMatch[1]),
    Number(dateMatch[2]) - 1,
    Number(dateMatch[3]),
    Number(timeMatch[1]),
    Number(timeMatch[2]),
    0,
    0,
  );
  return Number.isNaN(combined.getTime()) ? null : combined;
}
